import { ReactNode } from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical } from 'lucide-react'
import { cn } from '@/shared/lib/cn'

interface SortableBentoItemProps {
  id: string
  children: ReactNode
  className?: string
  colSpan?: 1 | 2 | 3 | 4 | 5 | 6
  rowSpan?: 1 | 2 | 3
}

export function SortableBentoItem({
  id,
  children,
  className,
  colSpan = 2,
  rowSpan = 1,
}: SortableBentoItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })

  // Fixed heights in pixels: base 280px per row + 16px gap per extra row
  // Only applied at md+ breakpoint; mobile uses auto height to prevent overlap
  const ROW_HEIGHT = 280
  const GAP = 16
  const fixedHeight = ROW_HEIGHT * rowSpan + GAP * (rowSpan - 1)

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const colSpanClasses = {
    1: 'col-span-1',
    2: 'col-span-1 md:col-span-2',
    3: 'col-span-1 md:col-span-2 lg:col-span-3',
    4: 'col-span-1 md:col-span-4',
    5: 'col-span-1 md:col-span-4 lg:col-span-5',
    6: 'col-span-1 md:col-span-4 lg:col-span-6',
  }

  const rowSpanClasses = {
    1: 'row-span-1',
    2: 'row-span-1 md:row-span-2',
    3: 'row-span-1 md:row-span-3',
  }

  return (
    <div
      ref={setNodeRef}
      style={{ ...style, ['--fixed-height' as string]: `${fixedHeight}px` }}
      className={cn(
        colSpanClasses[colSpan],
        rowSpanClasses[rowSpan],
        'relative group bento-item overflow-hidden',
        'min-h-[200px] md:h-[var(--fixed-height)]',
        isDragging && 'opacity-20',
        className
      )}
    >
      {/* Drag Handle */}
      <div
        {...attributes}
        {...listeners}
        className={cn(
          'absolute top-[15px] right-[15px] z-20 rounded-md',
          'text-[#888888] hover:text-white hover:bg-white/10 active:bg-white/20',
          'opacity-0 group-hover:opacity-100 md:transition-all md:duration-200',
          'cursor-grab active:cursor-grabbing touch-manipulation',
          // Larger touch target on mobile
          'p-2 md:p-1.5 min-w-[44px] min-h-[44px] md:min-w-0 md:min-h-0 flex items-center justify-center',
          // Hide handle when dragging (on the placeholder)
          isDragging && 'hidden'
        )}
        aria-label="Drag to reorder"
      >
        <GripVertical size={16} />
      </div>

      {children}
    </div>
  )
}
